const WORKFLOW_FILE_BASE_PATH = '.github/workflows/'
const WORKFLOWS_LAB_BASE_PATH = '.github/workflows-lab/'
const DYNAMIC_WORKFLOW_BASE_PATH = 'dynamic/'

export interface WorkflowRouteParams {
  owner?: string
  repo?: string
  workflowFileName?: string
  '*'?: string
}

interface WorkflowPath {
  path: string
  routePath?: string
}

export function workflowRouteParam({workflowFileName, '*': splat}: WorkflowRouteParams): string {
  return [workflowFileName, splat].filter(Boolean).join('/')
}

export function workflowRoutePath(workflow: string | WorkflowPath): string {
  const workflowPath = typeof workflow === 'string' ? workflow : (workflow.routePath ?? workflow.path)

  if (workflowPath.startsWith(WORKFLOW_FILE_BASE_PATH)) {
    return workflowPath.slice(WORKFLOW_FILE_BASE_PATH.length)
  }

  if (workflowPath.startsWith(DYNAMIC_WORKFLOW_BASE_PATH)) {
    return workflowPath.slice(DYNAMIC_WORKFLOW_BASE_PATH.length)
  }

  return workflowPath
}

export function workflowRouteMatchesPath(routePath: string, workflow: string | WorkflowPath): boolean {
  return workflowRoutePath(routePath) === workflowRoutePath(workflow)
}

// Whether a full workflow repo path (e.g. `workflow.path`) lives under the
// `.github/workflows-lab/` directory rather than the regular workflows directory.
export function isLabWorkflowPath(path: string): boolean {
  return path.startsWith(WORKFLOWS_LAB_BASE_PATH)
}

export function workflowFilePathFromRoute(routePath: string, {lab = false}: {lab?: boolean} = {}): string {
  // Lab workflows can be linked by bare filename with `lab=true` (e.g. from a run page),
  // so the flag decides which directory the filename lives in.
  if (!routePath.includes('/')) return `${lab ? WORKFLOWS_LAB_BASE_PATH : WORKFLOW_FILE_BASE_PATH}${routePath}`
  if (
    routePath.startsWith(DYNAMIC_WORKFLOW_BASE_PATH) ||
    routePath.startsWith('required/') ||
    routePath.startsWith(WORKFLOWS_LAB_BASE_PATH)
  ) {
    return routePath
  }

  return `${DYNAMIC_WORKFLOW_BASE_PATH}${routePath}`
}

export function workflowRouteHref(
  owner: string,
  repo: string,
  workflow: string | WorkflowPath,
  queryString = '',
): string {
  const encodedWorkflowPath = workflowRoutePath(workflow).split('/').map(encodeURIComponent).join('/')
  const search = new URLSearchParams(queryString)

  // Lab workflows live outside `.github/workflows/`; the server needs `lab=true` to
  // resolve the route path back to the right file instead of a regular workflow path.
  if (typeof workflow !== 'string' && isLabWorkflowPath(workflow.path)) {
    search.set('lab', 'true')
  }

  const searchString = search.toString()
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/actions/workflows/${encodedWorkflowPath}${searchString ? `?${searchString}` : ''}`
}
